// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — ProcessRefNode: Circle
// 100% sizing pattern (same as ActivityNode)
// ═══════════════════════════════════════════════════════════════

import { memo, useState, useCallback, useRef, useEffect } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, useReactFlow } from '@xyflow/react';
import { FLOW_TOKENS as T } from '../../shared/flowTokens';
import type { ProcessRefNodeData, FlowNodeData } from '../../types/flowchart.types';

const D = T.node.refRadius * 2;

function ProcessRefNodeComponent({ id, data, selected }: NodeProps) {
  const d = data as unknown as ProcessRefNodeData;
  const { updateNodeData } = useReactFlow();
  const [editValue, setEditValue] = useState(d.label);
  const inputRef = useRef<HTMLInputElement>(null);
  const editing = !!(d as any).editing;

  useEffect(() => {
    if (editing) {
      setEditValue(d.label);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing, d.label]);

  const saveEdit = useCallback(() => {
    updateNodeData(id, { label: editValue, editing: false } as Partial<FlowNodeData>);
  }, [id, editValue, updateNodeData]);

  const cancelEdit = useCallback(() => {
    updateNodeData(id, { editing: false } as Partial<FlowNodeData>);
  }, [id, updateNodeData]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'Enter') saveEdit();
      if (e.key === 'Escape') cancelEdit();
    },
    [saveEdit, cancelEdit],
  );

  const stroke = selected ? T.color.nodeStrokeSelected : T.color.nodeStroke;
  const strokeW = selected ? T.shape.strokeWidthSelected : T.shape.strokeWidth;

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <NodeResizer
        minWidth={40}
        minHeight={40}
        isVisible={!!selected && !!(d as any).resizeEnabled}
        lineClassName="node-resizer-line"
        handleClassName="node-resizer-handle"
      />
      <Handle
        type="target"
        position={Position.Top}
        id="top-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="top-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />

      <svg width="100%" height="100%" preserveAspectRatio="none" viewBox={`0 0 ${D} ${D}`}>
        <circle
          cx={D / 2}
          cy={D / 2}
          r={D / 2 - strokeW}
          fill={T.color.nodeFill}
          stroke={stroke}
          strokeWidth={strokeW}
        />
      </svg>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: T.font.family,
          pointerEvents: editing ? 'auto' : 'none',
          padding: '2px',
        }}
      >
        {d.procesoCodigo && !editing && (
          <span
            style={{
              fontSize: '7px',
              fontWeight: 600,
              color: T.color.textTertiary,
              lineHeight: 1.1,
            }}
          >
            {d.procesoCodigo}
          </span>
        )}
        {editing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={saveEdit}
            style={{
              fontSize: T.font.nodeTitle.size,
              fontWeight: T.font.nodeTitle.weight,
              fontFamily: T.font.family,
              color: T.color.textPrimary,
              textAlign: 'center',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              width: '80%',
              padding: 0,
            }}
          />
        ) : (
          <span
            style={{
              fontSize: T.font.nodeTitle.size,
              fontWeight: T.font.nodeTitle.weight,
              color: T.color.textSecondary,
              textAlign: 'center',
              lineHeight: 1.1,
              marginTop: 1,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
            }}
            title={d.label}
          >
            {d.label}
          </span>
        )}
      </div>
    </div>
  );
}

export const ProcessRefNode = memo(ProcessRefNodeComponent);
